'use strict';

import {ferry} from "../ferry/ferry";
import {sessionState} from "./sessionState";
import { account } from "../../fundus/account";

let loadingPromise: Promise<CompanyState>;
const MONTH_FIRST_DATE_FORMAT = 'MM/dd/yyyy';

export class CompanyState {
    id: string;
    dateformat: string;
    shortdate: string;
    shortdateshortyear: string;
    dateandtime: string;
    momentDateFormat: string;
    lowercasedate: string;
    formation: string;
    name: string;
    state_of_incorp: string;
    country: string;
    state: string;
    city: string;
    address: string;
    zipcode: string;
    currency: string;
    rounding_policy: string;
    shareprecision: number;
    show_investor_total_units: boolean;
    docs_with_tran: boolean;
    when_founded: Date;
    onboard_in_progress: boolean;
    valuation_customer_at_registration: boolean;
    billing_term_at_registration: string;
    share_fmv: boolean;
    launch: boolean;
    restricted_launch: boolean;
    doc_group_permissions: boolean;

    constructor() {
        this.id = undefined;
    }
    init() {
        this.id = sessionState.company;
        this.name = sessionState.storage_get("companyState-name");
        this.country = sessionState.storage_get("companyState-country");
        this.state = sessionState.storage_get("companyState-state");
        this.address = sessionState.storage_get("companyState-address");
        this.city = sessionState.storage_get("companyState-city");
        this.zipcode = sessionState.storage_get("companyState-zipcode");
        this.formation = sessionState.storage_get("companyState-formation");
        this.state_of_incorp = sessionState.storage_get("companyState-state_of_incorp");
        this.currency = sessionState.storage_get("companyState-currency");
        this.dateformat = sessionState.storage_get("companyState-dateformat");
        this.rounding_policy = sessionState.storage_get("companyState-rounding_policy");
        if (this.dateformat) {
            this.setDateFormats();
        }
        this.shareprecision = sessionState.storage_get("companyState-shareprecision");
        this.show_investor_total_units = sessionState.storage_get("companyState-show_investor_total_units");
        this.docs_with_tran = sessionState.storage_get("companyState-docs_with_tran");
        this.onboard_in_progress = sessionState.storage_get("companyState-onboard_in_progress");
        this.when_founded = sessionState.storage_get("companyState-when_founded");
        this.valuation_customer_at_registration = sessionState.storage_get("companyState-valuation_customer_at_registration");
        this.billing_term_at_registration = sessionState.storage_get("companyState-billing_term_at_registration");
        this.share_fmv = sessionState.storage_get("companyState-share_fmv");
        this.restricted_launch = sessionState.storage_get("companyState-restricted_launch");
        this.doc_group_permissions = sessionState.storage_get("companyState-doc_group_permissions");
        return this.loadFullCompany();
    }
    setDateFormats() {
        this.shortdate = this.dateformat === MONTH_FIRST_DATE_FORMAT ? MONTH_FIRST_DATE_FORMAT : 'dd/MM/yyyy';
        this.shortdateshortyear = this.dateformat === MONTH_FIRST_DATE_FORMAT ? 'MM/dd/yy' : 'dd/MM/yy';
        this.dateandtime = this.dateformat === MONTH_FIRST_DATE_FORMAT ? 'MMMM  dd y, h:mm a' : 'dd MMMM y, h:mm a';
        this.momentDateFormat = this.dateformat === MONTH_FIRST_DATE_FORMAT ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
        this.lowercasedate = this.dateformat.toLowerCase();
    }

    canHaveSubcompanies() {
        return this.formation === "LLC" || this.formation === "LLP";
    }

    isLL() {
        return this.formation === "LLC" || this.formation === "LLP";
    }

    isUSBasedCompany() {
        return this.country && this.country.trim() === 'US';
    }

    isDelawareCCorp() {
        return this.formation === "C-Corp" && this.state_of_incorp === 'DE';
    }

    refreshCompany() {
        loadingPromise = undefined;
        return this.loadFullCompany();
    }

    loadFullCompany() {
        if (!loadingPromise) {
            loadingPromise = ferry.selectone<account.my_company>('account.my_company').then((company) => {
                this.name = company.name;
                sessionState.storage_set("companyState-name", company.name);
                this.country = company.country;
                sessionState.storage_set("companyState-country", company.country);
                this.state = company.state;
                sessionState.storage_set("companyState-state", company.state);
                this.address = company.address;
                sessionState.storage_set("companyState-address", company.address);
                this.city = company.city;
                sessionState.storage_set("companyState-city", company.city);
                this.zipcode = company.zipcode;
                sessionState.storage_set("companyState-zipcode", company.zipcode);
                this.formation = company.formation;
                sessionState.storage_set("companyState-formation", company.formation);
                this.state_of_incorp = company.state_of_incorp;
                sessionState.storage_set("companyState-state_of_incorp", company.state_of_incorp);
                this.currency = company.currency;
                sessionState.storage_set("companyState-currency", company.currency);
                this.dateformat = company.dateformat;
                sessionState.storage_set("companyState-dateformat", company.dateformat);
                this.rounding_policy = company.rounding_policy;
                sessionState.storage_set("companyState-rounding_policy", company.rounding_policy);
                this.setDateFormats();
                this.shareprecision = company.shareprecision;
                sessionState.storage_set("companyState-shareprecision", company.shareprecision);
                this.show_investor_total_units = company.show_investor_total_units;
                sessionState.storage_set("companyState-show_investor_total_units", company.show_investor_total_units);
                this.docs_with_tran = company.docs_with_tran;
                sessionState.storage_set("companyState-docs_with_tran", company.docs_with_tran);
                this.onboard_in_progress = company.onboard_in_progress;
                sessionState.storage_set("companyState-onboard_in_progress", company.onboard_in_progress);
                this.when_founded = company.when_founded;
                sessionState.storage_set("companyState-when_founded", company.when_founded);
                this.valuation_customer_at_registration = company.valuation_customer_at_registration;
                sessionState.storage_set("companyState-valuation_customer_at_registration", company.valuation_customer_at_registration);
                this.billing_term_at_registration = company.billing_term_at_registration;
                sessionState.storage_set("companyState-billing_term_at_registration", company.billing_term_at_registration);
                this.share_fmv = company.share_fmv;
                sessionState.storage_set("companyState-share_fmv", company.share_fmv);
                this.launch = company.launch;
                sessionState.storage_set("companyState-launch", company.launch);
                this.restricted_launch = company.restricted_launch;
                sessionState.storage_set("companyState-restricted_launch", company.restricted_launch);
                this.doc_group_permissions = company.doc_group_permissions;
                sessionState.storage_set("companyState-doc_group_permissions", company.doc_group_permissions);
                return this;
            });
        }
        return loadingPromise;
    }
}

export let companyState = new CompanyState();



// WEBPACK FOOTER //
// ./_src/services/sessionState/companyState.ts